import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { elevateMfaSession, requiresMfaElevation } from '@/lib/mfaAssurance';

interface UseMfaOptions {
  enabled?: boolean;
}

export function useMfa(options: UseMfaOptions = {}) {
  const queriesEnabled = options.enabled ?? true;
  const queryClient = useQueryClient();

  const factors = useQuery({
    queryKey: ['mfa-factors'],
    enabled: queriesEnabled,
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data?.totp ?? [];
    },
  });

  const needsVerification = useQuery({
    queryKey: ['mfa-assurance'],
    enabled: queriesEnabled,
    queryFn: requiresMfaElevation,
  });

  const enroll = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        issuer: 'WorkLynx',
      });
      if (error) throw error;
      return {
        id: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mfa-factors'] });
    },
  });

  const verify = useMutation({
    mutationFn: async ({ factorId, code }: { factorId: string; code: string }) => {
      const { data: challengeData, error: challengeErr } =
        await supabase.auth.mfa.challenge({ factorId });
      if (challengeErr) throw challengeErr;

      const { data, error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['mfa-factors'] }),
        queryClient.invalidateQueries({ queryKey: ['mfa-assurance'] }),
      ]);
    },
  });

  const unenroll = useMutation({
    mutationFn: async ({ factorId, code }: { factorId: string; code: string }) => {
      await elevateMfaSession(code);
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['mfa-factors'] }),
        queryClient.invalidateQueries({ queryKey: ['mfa-assurance'] }),
      ]);
    },
  });

  const totpFactors = factors.data ?? [];
  const verifiedFactors = totpFactors.filter((factor) => factor.status === 'verified');

  return {
    factors: totpFactors,
    verifiedFactors,
    hasVerifiedFactor: verifiedFactors.length > 0,
    isLoadingFactors: factors.isLoading,
    error: factors.error ?? needsVerification.error ?? null,
    needsVerification: needsVerification.data ?? false,
    enroll,
    verify,
    unenroll,
  };
}
